<template>
  <div>
    <div class="fuel-consumption-list">
      <div class="mb-4">
        <span class="vessel-name-text"> {{ vesselName }} </span>
      </div>
      <ag-grid-vue
        :style="{ width, height }"
        ref="agGridTable"
        :rowHeight="rowHeight"
        :gridOptions="gridOptions"
        class="ag-theme-material ag-grid-table"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="voyageList"
        rowSelection="single"
        colResizeDefault="shift"
        :animateRows="false"
        :floatingFilter="false"
        :pagination="true"
        :paginationPageSize="999"
        :suppressPaginationPanel="true"
        :suppressDragLeaveHidesColumns="true"
        :suppressNoRowsOverlay="true"
        @grid-ready="onGridReady"
        @rowClicked="rowClicked"
      >
      </ag-grid-vue>
      <!-- pagenation -->
      <div id="pagenation-area" class="flex items-center justify-end mt-4">
        <!-- 件数変更 -->
        <div class="w-full">
          <div class="flex items-center justify-end">
            <div
              class="px-6 py-2 border border-solid d-theme-border-grey-light rounded-full flex items-center justify-center"
            >
              <span class="aisea-text-14-400-black">
                {{ startVoyageNum }}-{{ endVoyageNum }} / {{ totalVoyageCount }}
              </span>
            </div>
          </div>
        </div>
        <!-- ページ変更 -->
        <vs-pagination
          :total="totalPages"
          :color="AISEA_COLOR.BLUE"
          :max="maxPaginationCount"
          v-model="currentPage"
          class="pagination-view ml-4"
        ></vs-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import { DeviceInfoMixin, ColorMixin } from "@/mixins";
import moment from "moment-timezone";

import { createNamespacedHelpers } from "vuex";
const { mapState, mapActions, mapGetters } = createNamespacedHelpers("fuelConsumption");


export default {
  mixins: [DeviceInfoMixin, ColorMixin],
  components: {
    AgGridVue,
  },
  data() {
    return {
      gridApi: null,
      gridColumnApi: null,
      gridOptions: {
        localeText: { noRowsToShow: "表示する航海情報が見つかりませんでした。" },
        rowStyle: { cursor: "pointer" },
        context: {
          componentParent: this
        },
      },
      defaultColDef: {
        sortable: false,
        resizable: true,
      },
      height: "70vh",
      width: "100%",
      rowHeight: 48,
      columnDefs: null,
      funcSizeColumnsToFit: null,
      pageSize: 50,
      currentPage: 1,
    }
  },

  watch: {
    voyages: {
      handler(val) {
        if (val.length === 0) {
          this.onShowOverlayNoRows();
        }
      },
      deep: true
    },
    currentPage() {
      this.fetch();
    },
  },

  computed: {
    ...mapState(["voyages", "vessels", "voyageTotalPage", "voyageTotalElements"]),
    ...mapGetters(["getVessel"]),
    vesselName() {
      if (!this.vessel) return "";
      return this.vessel.name;
    },
    voyageList() {
      return this.voyages;
    },
    vesselId() {
      return this.$route.params.vesselId;
    },
    vessel() {
      return this.getVessel(this.vesselId);
    },

    totalVoyageCount() {
      if (!this.voyageTotalElements) return 0;
      return this.voyageTotalElements;
    },
    startVoyageNum() {
      return (this.currentPage - 1) * this.pageSize + 1;
    },
    endVoyageNum() {
      return Math.min(this.currentPage * this.pageSize, this.totalVoyageCount);
    },
    maxPaginationCount() {
      return 7;
    },
    totalPages() {
      if (!this.voyageTotalPage) return 0;
      return this.voyageTotalPage;
    },
    positionNameCellSize() {
      if (this.$store.state.windowWidth < 500) {
        return {
          min: 60,
          max: 100,
        }
      }
      return {
        min: 100,
        max: 150,
      }
    },
    timeCellSize() {
      if (this.$store.state.windowWidth < 500) {
        return {
          min: 150,
          max: 200,
        }
      }
      return {
        min: 170,
        max: 200,
      }
    },
  },

  methods: {

    datetimeFormatter(params) {
      return moment(params.value).format("YYYY-MM-DD HH:mm")
    },
    ...mapActions(["fetchVoyageList", "fetchAllVessels"]),
    async onGridReady(params) {
      if (params) {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;
        this.gridApi.sizeColumnsToFit();

        this.funcSizeColumnsToFit = () => {
          setTimeout(() => {
            params.api.sizeColumnsToFit();
          })
        };

        window.onresize = this.funcSizeColumnsToFit
      }
    },

    onShowOverlayNoRows() {
      this.gridOptions.suppressNoRowsOverlay = false;
      this.gridApi.showNoRowsOverlay();
    },

    rowClicked(event) {
      this.$router.push({
        name: "fuel-consumption-detail-voyage",
        params: { vesselId: this.vesselId, voyageId: event.data.voyage_history_id }
      });
    },
    async fetch() {
      try {
        this.$vs.loading();
        if (this.vessels.length === 0) {
          await this.fetchAllVessels();
        }
        const payload = {
          vesselId: this.vesselId,
          page: this.currentPage - 1
        }
        await this.fetchVoyageList(payload);
        this.$vs.loading.close();
      } catch (error) {
        console.error(error);
        this.$vs.loading.close();
        let msg = error.msg;
        if (!msg) {
          msg = "予期せぬエラーが発生しました。";
        }
        this.$vs.notify({
          title: msg,
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
      }
    },
    initLayout() {
      let routerViewElement = document.getElementsByClassName("router-view");
      routerViewElement[0].style.padding = "2.2rem";
    },
  },

  beforeMount() {
    this.columnDefs = [
      {
        headerName: "出発港",
        sortable: false,
        field: "start_position_name",
        colId: "start_position_name",
        minWidth: this.positionNameCellSize.min,
        maxWidth: this.positionNameCellSize.max
      },
      {
        headerName: "到着港",
        sortable: false,
        field: "goal_position_name",
        colId: "goal_position_name",
        minWidth: this.positionNameCellSize.min,
        maxWidth: this.positionNameCellSize.max,
      },
      {
        headerName: "出発時間",
        sortable: false,
        field: "start_time",
        colId: "start_time",
        minWidth: this.timeCellSize.min,
        maxWidth: this.timeCellSize.max,
        valueFormatter: this.datetimeFormatter
      },
      {
        headerName: "到着時間",
        sortable: false,
        field: "goal_time",
        colId: "goal_time",
        minWidth: this.timeCellSize.min,
        maxWidth: this.timeCellSize.max,
        valueFormatter: this.datetimeFormatter
      },
      {
        headerName: "",
        sortable: false,
        field: "_",
        minWidth: 1,
        maxWidth: 2000,
      }, // 余白を埋める用
    ];
  },

  created() {
    this.fetch();
  },

  mounted() {
    this.initLayout();
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
  },

  beforeDestroy() {
    window.onresize = null;
  }
}
</script>


<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";
@import "@/assets/scss/aisea/_typography.scss";

.fuel-consumption-list {
  font-family: $aisea-font-family;
  background: $aisea-pure-white;
  border-radius: 12px;
  padding: 40px 30px;

  .vessel-name-text {
    font-size: 24px;
    font-weight: 600;
  }

  .ag-header-active {
    background-color: transparent !important;
  }

  .pagination-view {
    width: auto !important;
    .vs-pagination--mb {
      margin: 0;
    }
  }
}

@media (max-width: 500px) {
  .fuel-consumption-list {
    padding: 16px;
    .ag-header-cell.ag-focus-managed {
      padding: 8px;
    }
    .ag-cell-value {
      padding-right: 8px;
      padding-left: 8px;
    }
  }
}
</style>